import { onScopeDispose } from 'vue';

/** A slice of rows: `limit` slots from `offset` on. */
export type NxTableRange = { offset: number; limit: number };

export type NxTableRangeLoaderOptions = {
  /** Number of row slots — loaded and still missing. */
  length: () => number;
  /** Whether slot `index` still has to be loaded (a placeholder). */
  isMissing: (index: number) => boolean;
  /**
   * Loads `range` and merges it into the consumer's rows. `isCurrent()` turns
   * false once `reset()` started a new generation (another query, a reseed):
   * the answer then belongs to rows that no longer exist and must be dropped.
   */
  load: (range: NxTableRange, isCurrent: () => boolean) => Promise<void>;
  /** Largest range per request (default 40). */
  maxRange?: number;
  /** Slots loaded beyond the visible band on each side (default 10). */
  overscan?: number;
  /** A visible band settles this long before it loads (default 120 ms). */
  debounceMs?: number;
  /**
   * Align requests to pages of this size (default 1 = no alignment) — for a
   * backend that serves whole pages, like mail's message list.
   */
  pageSize?: number;
  /**
   * A request still open after this long (default 30 s) no longer holds up the
   * queue — the next load starts, and its late answer counts as stale.
   */
  requestTimeoutMs?: number;
};

/**
 * Loads the missing slots of a windowed/sparse table on demand (I-2172): the
 * consumer owns the rows and their placeholders, this plans the requests —
 * the holes of the visible band plus overscan, coalesced into one span, split
 * at `maxRange` (and aligned to `pageSize`), debounced while scrolling. Every
 * entry (visible band, `ensureRange`, `ensureIndex`) shares one queue, so there
 * is never more than one request in flight; the latest visible band wins, an
 * overtaken band stops before its next chunk; and generations make sure an
 * answer to an older query never lands in newer rows.
 *
 * Wire `requestVisible` to NxTable's `visible-range-change` (or its
 * `virtualWindow`), call `reset()` whenever the rows are reseeded.
 */
export function useNxTableRangeLoader(options: NxTableRangeLoaderOptions) {
  const maxRange = Math.max(1, Math.floor(options.maxRange ?? 40));
  const overscan = Math.max(0, Math.floor(options.overscan ?? 10));
  const debounceMs = Math.max(0, options.debounceMs ?? 120);
  const pageSize = Math.max(1, Math.floor(options.pageSize ?? 1));
  const requestTimeoutMs = Math.max(0, options.requestTimeoutMs ?? 30_000);

  let generation = 0;
  let debounce: ReturnType<typeof setTimeout> | undefined;
  let pendingBand: { start: number; end: number } | null = null;
  let lastBandKey = '';
  /** Every load runs through this chain: one request at a time, in call order. */
  let queue: Promise<void> = Promise.resolve();
  /** A band task is queued and has not started yet. */
  let bandTaskQueued = false;

  function enqueue(task: () => Promise<void>): Promise<void> {
    const run = queue.then(task);
    queue = run.catch(() => {});
    return run;
  }

  function clamp(start: number, end: number): { start: number; end: number } {
    const length = options.length();
    return {
      start: Math.max(0, Math.floor(start)),
      end: Math.min(length, Math.floor(end)),
    };
  }

  /** The one span covering every missing slot in [start, end), page-aligned. */
  function missingSpan(start: number, end: number): NxTableRange | null {
    let first = -1;
    let last = -1;
    for (let i = start; i < end; i++) {
      if (!options.isMissing(i)) continue;
      if (first < 0) first = i;
      last = i;
    }
    if (first < 0) return null;
    const alignedStart = Math.floor(first / pageSize) * pageSize;
    const alignedEnd = Math.min(options.length(), Math.ceil((last + 1) / pageSize) * pageSize);
    return { offset: alignedStart, limit: alignedEnd - alignedStart };
  }

  function hasMissing(start: number, end: number): boolean {
    for (let i = start; i < end; i++) if (options.isMissing(i)) return true;
    return false;
  }

  /**
   * One `load`, bounded: once `requestTimeoutMs` passed it resolves anyway so
   * the queue moves on, and the answer, whenever it comes, is stale.
   */
  function runLoad(range: NxTableRange, isCurrent: () => boolean): Promise<void> {
    let expired = false;
    const stillCurrent = () => !expired && isCurrent();
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        expired = true;
        console.warn('[NxTable] range load timed out', range);
        resolve();
      }, requestTimeoutMs);
      options.load(range, stillCurrent).then(
        () => {
          clearTimeout(timer);
          resolve();
        },
        (error: unknown) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * Loads every missing slot of [offset, offset + limit), chunk by chunk, while
   * `keepGoing()` holds. Runs inside the queue only.
   */
  async function loadSpan(
    offset: number,
    limit: number,
    isCurrent: () => boolean,
    keepGoing: () => boolean
  ): Promise<void> {
    const band = clamp(offset, offset + Math.max(1, Math.floor(limit)));
    if (band.end <= band.start) return;
    const span = missingSpan(band.start, band.end);
    if (!span) return;
    const chunk = Math.max(pageSize, Math.floor(maxRange / pageSize) * pageSize);
    for (let from = span.offset; from < span.offset + span.limit; from += chunk) {
      if (!isCurrent() || !keepGoing()) return;
      const to = Math.min(span.offset + span.limit, from + chunk);
      // An earlier chunk's answer (or another request) may have filled it.
      if (!hasMissing(from, to)) continue;
      try {
        await runLoad({ offset: from, limit: to - from }, isCurrent);
      } catch (error) {
        console.error('[NxTable] range load failed', { offset: from, limit: to - from }, error);
      }
    }
  }

  /**
   * Loads every missing slot of [offset, offset + limit). Queued behind any
   * request in flight — never a second request next to it.
   */
  function ensureRange(offset: number, limit: number): Promise<void> {
    const current = generation;
    const isCurrent = () => current === generation;
    return enqueue(() => loadSpan(offset, limit, isCurrent, isCurrent));
  }

  /** Fills a window around `index` if that slot is still missing. */
  async function ensureIndex(index: number): Promise<void> {
    const length = options.length();
    if (length === 0) return;
    const i = Math.max(0, Math.min(length - 1, Math.floor(index)));
    if (!options.isMissing(i)) return;
    await ensureRange(Math.max(0, i - Math.floor(maxRange / 2)), maxRange);
  }

  /**
   * Queues one task for the latest visible band. A band that is overtaken by a
   * newer one while it loads stops before its next chunk: rows the user has
   * scrolled past are not worth the wait of the rows now on screen.
   */
  function flush(): void {
    if (bandTaskQueued) return;
    bandTaskQueued = true;
    void enqueue(async () => {
      bandTaskQueued = false;
      const band = pendingBand;
      pendingBand = null;
      if (!band) return;
      const current = generation;
      const isCurrent = () => current === generation;
      await loadSpan(band.start, band.end - band.start, isCurrent, () => pendingBand === null);
      // A newer band arrived meanwhile: its own task is queued already.
    });
  }

  /**
   * The rows [start, end) are on screen: load their holes plus overscan.
   * Debounced while scrolling; `immediate` loads right away (first screen).
   */
  function requestVisible(start: number, end: number, opts?: { immediate?: boolean }): void {
    if (options.length() === 0) return;
    const band = clamp(start - overscan, end + overscan);
    const key = `${band.start}:${band.end}`;
    // The same, already complete viewport: nothing to do, no debounce thrash.
    if (key === lastBandKey && !hasMissing(band.start, band.end)) return;
    lastBandKey = key;
    pendingBand = band;
    if (debounce) clearTimeout(debounce);
    debounce = undefined;
    if (opts?.immediate || debounceMs === 0) {
      flush();
      return;
    }
    debounce = setTimeout(() => {
      debounce = undefined;
      flush();
    }, debounceMs);
  }

  /** The rows were reseeded: drop pending work and every in-flight answer. */
  function reset(): void {
    generation++;
    // A new generation does not wait for the old one's requests.
    queue = Promise.resolve();
    bandTaskQueued = false;
    pendingBand = null;
    lastBandKey = '';
    if (debounce) clearTimeout(debounce);
    debounce = undefined;
  }

  onScopeDispose(reset);

  return { requestVisible, ensureRange, ensureIndex, reset };
}
